import axios from 'axios';
import { getApiBaseUrl } from '../utils/api';

const API_BASE_URL = getApiBaseUrl();

// Create axios instance
const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor
axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            window.location.href = '/app/login';
        }
        return Promise.reject(error);
    }
);

/**
 * Org Chart Service
 * Handles API calls for organizational chart and MLM performance integration
 */
export const orgChartService = {
    /**
     * Get MLM performance data for a position (with filtering)
     * @param {number|null} positionId - Position ID to get performance for (null = authenticated user's position)
     * @param {string} filter - Filter type: 'whole_branch' or 'this_position_only'
     * @returns {Promise} Position MLM performance data
     */
    getPositionMLMPerformance: async (positionId = null, filter = 'whole_branch') => {
        try {
            console.log('orgChartService: getPositionMLMPerformance called with positionId:', positionId, 'filter:', filter);

            // Build request payload
            const payload = {
                filter: filter
            };

            // Only include position_id if it's provided and valid
            if (positionId !== null && positionId !== undefined) {
                // Ensure it's an integer
                payload.position_id = parseInt(positionId, 10);
                console.log('orgChartService: Parsed positionId to:', payload.position_id, 'Type:', typeof payload.position_id);
            }

            console.log('orgChartService: Sending payload:', payload);
            const response = await axiosInstance.post('/tree/org-chart/position-mlm-performance', payload);
            console.log('orgChartService: Response received:', response.data);
            return response.data;
        } catch (error) {
            console.error('orgChartService: API error:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get user's MLM tree (for modal deep dive feature)
     * @param {number} userId - User ID whose MLM tree to fetch
     * @returns {Promise} MLM tree structure
     */
    getUserMLMTree: async (userId) => {
        try {
            console.log('orgChartService: getUserMLMTree called with userId:', userId);

            const parsedUserId = parseInt(userId, 10);
            console.log('orgChartService: Sending GET request with user_id:', parsedUserId);

            const response = await axiosInstance.get('/tree/org-chart/user-mlm-tree', {
                params: {
                    user_id: parsedUserId
                }
            });

            console.log('orgChartService: Response received:', response.data);
            return response.data;
        } catch (error) {
            console.error('orgChartService: API error:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get node performance data (for modal MLM tree nodes)
     * @param {number} userId - User ID whose performance to fetch
     * @returns {Promise} Node performance data
     */
    getNodePerformance: async (userId) => {
        try {
            console.log('orgChartService: getNodePerformance called with userId:', userId);

            const parsedUserId = parseInt(userId, 10);
            console.log('orgChartService: Sending GET request with user_id:', parsedUserId);

            const response = await axiosInstance.get('/tree/org-chart/node-performance', {
                params: {
                    user_id: parsedUserId
                }
            });

            console.log('orgChartService: Response received:', response.data);
            return response.data;
        } catch (error) {
            console.error('orgChartService: API error:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Search org chart positions by position name, code, or assigned user
     * @param {string} query - Search query (minimum 2 characters)
     * @param {string} searchType - Search type: 'position' or 'user' (default: 'position')
     * @param {number} limit - Maximum number of results (default: 10)
     * @returns {Promise} Search results
     */
    searchPositions: async (query, searchType = 'position', limit = 10) => {
        try {
            console.log('orgChartService: searchPositions called with query:', query, 'searchType:', searchType, 'limit:', limit);

            const response = await axiosInstance.get('/tree/org-chart/search', {
                params: {
                    q: query,
                    search_type: searchType,
                    limit: limit
                }
            });

            console.log('orgChartService: searchPositions response:', response.data);
            return response.data;
        } catch (error) {
            console.error('orgChartService: searchPositions error:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get hierarchical path from root to target position (for auto-navigation)
     * @param {number} positionId - Target position ID
     * @returns {Promise} Path data with position hierarchy
     */
    getPositionPath: async (positionId) => {
        try {
            console.log('orgChartService: getPositionPath called with positionId:', positionId);

            const parsedPositionId = parseInt(positionId, 10);
            const response = await axiosInstance.get(`/tree/org-chart/path/${parsedPositionId}`);

            console.log('orgChartService: getPositionPath response:', response.data);
            return response.data;
        } catch (error) {
            console.error('orgChartService: getPositionPath error:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get child positions for lazy loading with pagination
     * @param {number} positionId - Parent position ID
     * @param {number} offset - Offset for pagination (default: 0)
     * @param {number} limit - Limit per page (default: 50)
     * @returns {Promise} Children data with pagination info
     */
    getPositionChildren: async (positionId, offset = 0, limit = 50) => {
        try {
            console.log('orgChartService: getPositionChildren called with positionId:', positionId, 'offset:', offset, 'limit:', limit);

            const parsedPositionId = parseInt(positionId, 10);
            const response = await axiosInstance.get(`/tree/org-chart/${parsedPositionId}/children`, {
                params: {
                    offset: offset,
                    limit: limit
                }
            });

            console.log('orgChartService: getPositionChildren response:', response.data);
            return response.data;
        } catch (error) {
            console.error('orgChartService: getPositionChildren error:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Search users in MLM network via org chart (for MLM modal search)
     * Allows searching within a specific user's MLM network based on org chart access permissions
     * @param {number} treeOwnerUserId - User ID whose MLM network to search in
     * @param {string} query - Search query (minimum 2 characters)
     * @param {number} limit - Maximum number of results (default: 10)
     * @returns {Promise} Search results
     */
    searchUserMLMViaOrgChart: async (treeOwnerUserId, query, limit = 10) => {
        try {
            console.log('orgChartService: searchUserMLMViaOrgChart called with treeOwnerUserId:', treeOwnerUserId, 'query:', query, 'limit:', limit);

            const response = await axiosInstance.post('/tree/org-chart/user-mlm-search', {
                tree_owner_user_id: parseInt(treeOwnerUserId, 10),
                query: query,
                limit: limit
            });

            console.log('orgChartService: searchUserMLMViaOrgChart response:', response.data);
            return response.data;
        } catch (error) {
            console.error('orgChartService: searchUserMLMViaOrgChart error:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get hierarchical path from tree owner to target user (for MLM modal navigation)
     * Used for auto-navigation when clicking search results
     * @param {number} treeOwnerUserId - User ID who owns the MLM tree (root of the view)
     * @param {number} targetUserId - Target user ID to navigate to
     * @returns {Promise} Path data with user hierarchy
     */
    getUserPathViaOrgChart: async (treeOwnerUserId, targetUserId) => {
        try {
            console.log('orgChartService: getUserPathViaOrgChart called with treeOwnerUserId:', treeOwnerUserId, 'targetUserId:', targetUserId);

            const response = await axiosInstance.post('/tree/org-chart/user-mlm-path', {
                tree_owner_user_id: parseInt(treeOwnerUserId, 10),
                target_user_id: parseInt(targetUserId, 10)
            });

            console.log('orgChartService: getUserPathViaOrgChart response:', response.data);
            return response.data;
        } catch (error) {
            console.error('orgChartService: getUserPathViaOrgChart error:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get position user's MLM tree (SHALLOW - for lazy loading modal)
     * Returns shallow MLM tree for modal initial load - root + direct children only
     * @param {number} positionId - Org chart position ID
     * @returns {Promise} Shallow MLM tree structure
     */
    getPositionUserMLMTreeShallow: async (positionId) => {
        try {
            console.log('orgChartService: getPositionUserMLMTreeShallow called with positionId:', positionId);

            const parsedPositionId = parseInt(positionId, 10);
            const response = await axiosInstance.get(`/tree/org-chart/position/${parsedPositionId}/user-mlm-tree-shallow`);

            console.log('orgChartService: getPositionUserMLMTreeShallow response:', response.data);
            return response.data;
        } catch (error) {
            console.error('orgChartService: getPositionUserMLMTreeShallow error:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get MLM children for a user (accessed via org chart)
     * Returns paginated children for lazy loading in MLM modal
     * Security: Same as node performance - validates user in org chart branch MLM network
     * @param {number} userId - Parent user ID whose children to load
     * @param {number} offset - Pagination offset (default: 0)
     * @param {number|null} limit - Pagination limit (default: null = all children)
     * @returns {Promise} Children with pagination metadata
     */
    getPositionUserMLMChildren: async (userId, offset = 0, limit = null) => {
        try {
            console.log('orgChartService: getPositionUserMLMChildren called with userId:', userId, 'offset:', offset, 'limit:', limit);

            const parsedUserId = parseInt(userId, 10);

            const params = { offset };
            if (limit !== null) {
                params.limit = limit;
            }

            const response = await axiosInstance.get(
                `/tree/org-chart/user-mlm-children/${parsedUserId}`,
                { params }
            );

            console.log('orgChartService: getPositionUserMLMChildren response:', response.data);
            return response.data;
        } catch (error) {
            console.error('orgChartService: getPositionUserMLMChildren error:', error);
            throw error.response?.data || error;
        }
    }
};

export default orgChartService;
